import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export interface ExcelColumn {
  header: string;
  key: string;
  width?: number;
}

export interface ExcelExportOptions {
  fileName: string;
  sheetName?: string;
  columns: ExcelColumn[];
  data: Record<string, unknown>[];
  styles?: {
    headerBackgroundColor?: string;
    showBorders?: boolean;
  };
}

/**
 * Utilitario para exportar datos a Excel usando ExcelJS
 */
export class ExcelExporter {
  /**
   * Exporta datos a un archivo Excel
   */
  static async exportToExcel(options: ExcelExportOptions): Promise<void> {
    const {
      fileName,
      sheetName = 'Datos',
      columns,
      data,
      styles = {
        headerBackgroundColor: 'FFE6E6FA',
        showBorders: true,
      },
    } = options;

    try {
      // Crear un nuevo workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(sheetName);

      // Definir las columnas
      worksheet.columns = columns;

      // Agregar los datos
      data.forEach((row) => {
        worksheet.addRow(row);
      });

      // Aplicar estilos al header
      if (styles.headerBackgroundColor) {
        worksheet.getRow(1).font = { bold: true };
        worksheet.getRow(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: styles.headerBackgroundColor },
        };
      }

      // Aplicar bordes si está habilitado
      if (styles.showBorders) {
        worksheet.eachRow((row) => {
          row.eachCell((cell) => {
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' },
            };
          });
        });
      }

      // Generar el buffer del archivo Excel
      const buffer = await workbook.xlsx.writeBuffer();

      // Crear el blob con el tipo MIME correcto
      const blob = new globalThis.Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Verificar que el blob se creó correctamente
      if (blob.size === 0) {
        throw new Error('El archivo generado está vacío');
      }

      console.log('Archivo Excel generado:', {
        size: blob.size,
        type: blob.type,
        fileName: fileName,
      });

      // Usar file-saver para descargar el archivo
      saveAs(blob, fileName);
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      throw error;
    }
  }

  /**
   * Crea columnas estándar para lecturas de dispositivos
   */
  static createLecturesColumns(): ExcelColumn[] {
    return [
      { header: 'ID Lectura', key: 'lectureId', width: 15 },
      { header: 'ID Operación', key: 'operationId', width: 15 },
      { header: 'Secuencia', key: 'sequence', width: 10 },
      { header: 'Fecha/Hora', key: 'datetime', width: 22 },
      { header: 'Zona', key: 'timezoneShort', width: 8 },
      { header: 'Timestamp (UTC)', key: 'timestamp', width: 24 },
      { header: 'Latitud', key: 'latitude', width: 12 },
      { header: 'Longitud', key: 'longitude', width: 12 },
      { header: 'Tipo Evento', key: 'eventType', width: 15 },
      { header: 'Ángulo Lateral', key: 'angleLateral', width: 12 },
      { header: 'Ángulo Longitudinal', key: 'angleLongitudinal', width: 12 },
      { header: 'Límite Lateral', key: 'limitLateral', width: 12 },
      { header: 'Límite Longitudinal', key: 'limitLongitudinal', width: 12 },
    ];
  }

  /**
   * Crea columnas estándar para dispositivos
   */
  static createDevicesColumns(): ExcelColumn[] {
    return [
      { header: 'Número de Serie', key: 'serialNumber', width: 15 },
      { header: 'Patente', key: 'plate', width: 12 },
      { header: 'Alias', key: 'alias', width: 15 },
      { header: 'Tipo', key: 'type', width: 12 },
      { header: 'Estado', key: 'status', width: 12 },
      { header: 'Última Conexión', key: 'lastConnection', width: 20 },
      { header: 'Zona C', key: 'lastConnectionZone', width: 10 },
      { header: 'Última Operación', key: 'lastOperation', width: 20 },
      { header: 'Zona O', key: 'lastOperationZone', width: 10 },
      { header: 'Cantidad Operaciones', key: 'operationCount', width: 15 },
    ];
  }

  /**
   * Crea columnas estándar para operaciones
   */
  static createOperationsColumns(): ExcelColumn[] {
    return [
      { header: 'ID Operación', key: 'operationId', width: 15 },
      { header: 'Fecha Inicio', key: 'startDate', width: 20 },
      { header: 'Fecha Fin', key: 'endDate', width: 20 },
      { header: 'Duración (min)', key: 'duration', width: 12 },
      { header: 'Cantidad Lecturas', key: 'lectureCount', width: 15 },
      { header: 'Latitud Centroide', key: 'centroidLat', width: 15 },
      { header: 'Longitud Centroide', key: 'centroidLng', width: 15 },
    ];
  }

  /**
   * Genera un nombre de archivo con fecha
   */
  static generateFileName(prefix: string, identifier?: string): string {
    const date = new Date().toISOString().split('T')[0];
    return identifier ? `${prefix}_${identifier}_${date}.xlsx` : `${prefix}_${date}.xlsx`;
  }
}
